import React, {useState, useCallback, useRef} from 'react';
import styled, {css} from 'styled-components';
import {useIntl} from 'react-intl';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {D} from '../ai-chat/design-tokens';
import KebabMenu from './KebabMenu';

const {font} = tokensForThemeProvider.sky.sys;

const labelFont = css`
	${font.label['1']}
`;

const Header = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	height: 60px;
	padding: 0 24px;
	border-bottom: 1px solid ${D.inputBorder};
	flex-shrink: 0;
`;

const Title = styled.span`
	${labelFont}
	flex: 1;
	color: ${D.v2.textPrimary};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const IconBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: none;
	background: transparent;
	border-radius: 8px;
	cursor: pointer;
	color: ${D.v2.textPrimary};
	flex-shrink: 0;
	overflow: clip;

	&:hover {
		background: ${D.v2.surfaceHover};
	}
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

type DrawerHeaderProps = {
	onClose: () => void;
	onClearChat: () => void;
	onRegenerateLastReply: () => void;
	canRegenerate: boolean;
};

const DrawerHeader = ({onClose, onClearChat, onRegenerateLastReply, canRegenerate}: DrawerHeaderProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [isKebabOpen, setIsKebabOpen] = useState(false);
	const kebabBtnRef = useRef<HTMLButtonElement>(null);
	const handleCloseKebab = useCallback(() => setIsKebabOpen(false), []);

	return (
		<Header>
			<Title>{formatMessage({id: 'nova.header.title'})}</Title>
			<Actions>
				<IconBtn
					ref={kebabBtnRef}
					type="button"
					aria-label={formatMessage({id: 'nova.header.kebab_label'})}
					onClick={() => setIsKebabOpen(prev => !prev)}
				>
					<Icon icon="more" size="m" />
				</IconBtn>
				<IconBtn type="button" aria-label={formatMessage({id: 'nova.drawer.close_label'})} onClick={onClose}>
					<Icon icon="close" size="m" />
				</IconBtn>
			</Actions>
			{isKebabOpen && (
				<KebabMenu
					onClearChat={onClearChat}
					onRegenerateLastReply={onRegenerateLastReply}
					canRegenerate={canRegenerate}
					onClose={handleCloseKebab}
					excludeRef={kebabBtnRef}
				/>
			)}
		</Header>
	);
};

export default DrawerHeader;
